/**
 * Kickoff parsing and fixture status, shared by the PL and NFL boxes.
 *
 * The two games emit kickoffs differently. The PL sends a timezone-aware
 * value ending in "+00:00"; the NFL stores kickoffs naive-UTC and serialises
 * them with no offset at all. JavaScript reads an offset-less datetime as
 * *local* time, so an NFL kickoff parsed with a bare `new Date()` lands hours
 * off — 17:00 UTC showing as 5:00 PM in New York rather than 1:00 PM.
 *
 * Everything here goes through parseKickoff so both shapes mean UTC.
 */

/** Parse a kickoff string as UTC, whatever shape the API sent. */
export function parseKickoff(iso) {
  if (!iso) return null;
  const hasZone = iso.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(iso);
  const d = new Date(hasZone ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Kickoff in the viewer's local zone, e.g. "Sat, 8/22, 3:00 PM". */
export function formatKickoff(iso) {
  const d = parseKickoff(iso);
  if (!d) return "";
  return d.toLocaleString("en-US", {
    weekday: "short", month: "numeric", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

/**
 * Fixtures a provider has called off. Kept apart from the clock because a
 * called-off game's start time passes like any other, and inferring from the
 * clock alone then reports it as being played. MLB spells "cancelled" with
 * one l and two at different times, so both are listed.
 */
const NOT_PLAYED = {
  Cancelled: "Cancelled",
  Canceled: "Cancelled",
  Postponed: "Postponed",
  Suspended: "Suspended",
};

/**
 * Where a fixture is in its life: Upcoming, In Progress, Final, or called off.
 *
 * "In Progress" means kicked off but not yet marked final. Because ingest
 * runs hourly, a match can sit in that state for a while after the real final
 * whistle — which is why this deliberately shows no score. The status is
 * honest about having started; a stale scoreline would not be.
 *
 * `state` is the provider's own status string, and takes precedence over the
 * clock when it says the game is not being played. Without it this function
 * behaves exactly as before, which is what the PL and NFL boxes rely on.
 */
export function fixtureStatus(iso, isFinal, now = Date.now(), state = null) {
  if (isFinal) return { label: "Final", color: "green.600" };
  const calledOff = state ? NOT_PLAYED[String(state).trim()] : null;
  if (calledOff) return { label: calledOff, color: "gray.500" };
  const d = parseKickoff(iso);
  if (d && d.getTime() <= now) {
    return { label: "In Progress", color: "orange.500" };
  }
  return { label: "Upcoming", color: "gray.600" };
}
